import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Mic, Menu, X, Bell, Search, Crown, Home, Compass, Music2, Users, Upload } from "lucide-react";
import Logo from "./Logo";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

const PUBLIC_LINKS = [
  { to: "/discover", label: "Discover", icon: Compass },
  { to: "/songs", label: "Songs", icon: Music2 },
  { to: "/community", label: "Community", icon: Users },
  { to: "/premium", label: "Premium", icon: Crown },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);

  useEffect(() => { setOpen(false); }, [loc.pathname]);
  useEffect(() => {
    if (user) api.get("/me/notifications").then(({ data }) => setUnread(data.unread)).catch(() => {});
  }, [user, loc.pathname]);

  const avatarSrc = user?.avatar_url
    ? (user.avatar_url.startsWith("http") ? user.avatar_url : `${process.env.REACT_APP_BACKEND_URL}${user.avatar_url}`)
    : "";

  return (
    <header className="sticky top-0 z-50 glass border-b border-white/10">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
        <div className="flex items-center gap-8">
          <Logo size={40} showText />
          <div className="hidden lg:flex items-center gap-1">
            {PUBLIC_LINKS.map((l) => (
              <Link key={l.to} to={l.to} data-testid={`nav-${l.label.toLowerCase()}`}
                className={`px-3 py-2 rounded-full text-sm font-medium transition-colors ${
                  loc.pathname === l.to ? "text-white bg-white/10" : "text-muted-foreground hover:text-white hover:bg-white/5"
                }`}>
                {l.label}
              </Link>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Link to="/record" data-testid="nav-record-btn"
            className="hidden sm:flex items-center gap-2 px-5 py-2.5 rounded-full bg-neon-pink text-white text-sm font-bold glow-pink hover:scale-105 transition-transform">
            <Mic className="w-4 h-4" /> Record
          </Link>

          {user ? (
            <>
              <Link to="/notifications" data-testid="nav-notifications" className="relative p-2 rounded-full hover:bg-white/10 transition-colors">
                <Bell className="w-5 h-5" />
                {unread > 0 && <span className="absolute top-1 right-1 w-2.5 h-2.5 bg-neon-pink rounded-full" />}
              </Link>
              <DropdownMenu>
                <DropdownMenuTrigger data-testid="nav-user-menu" className="outline-none">
                  <Avatar className="w-9 h-9 border border-white/20">
                    <AvatarImage src={avatarSrc} />
                    <AvatarFallback className="bg-neon-purple/30 text-white text-sm">
                      {(user.display_name || user.username || "U")[0].toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 glass border-white/10">
                  <div className="px-3 py-2">
                    <p className="text-sm font-semibold">{user.display_name}</p>
                    <p className="text-xs text-muted-foreground">@{user.username}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => nav(`/@${user.username}`)} data-testid="menu-profile">Profile</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => nav("/recordings")} data-testid="menu-recordings">My Recordings</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => nav("/saved")} data-testid="menu-saved">Saved Songs</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => nav("/upload")}>Upload</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => nav("/billing")} data-testid="menu-billing">Membership & Billing</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => nav("/settings")} data-testid="menu-settings">Settings</DropdownMenuItem>
                  {user.role === "admin" && <DropdownMenuItem onClick={() => nav("/admin")} data-testid="menu-admin">Admin Console</DropdownMenuItem>}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => { logout(); nav("/"); }} data-testid="menu-logout" className="text-neon-pink">Logout</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login" className="hidden sm:block px-4 py-2 text-sm font-medium text-muted-foreground hover:text-white transition-colors">Log in</Link>
              <Link to="/register" data-testid="nav-join" className="px-5 py-2.5 rounded-full glass text-sm font-bold hover:bg-white/10 transition-colors whitespace-nowrap">Join Free</Link>
            </>
          )}

          <button className="lg:hidden p-2" onClick={() => setOpen(!open)} data-testid="mobile-menu-btn">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </nav>

      {open && (
        <div className="lg:hidden glass border-t border-white/10 px-4 py-4 space-y-1">
          {[{ to: "/", label: "Home", icon: Home }, ...PUBLIC_LINKS, { to: "/record", label: "Record", icon: Mic }, { to: "/upload", label: "Upload", icon: Upload }].map((l) => (
            <Link key={l.to} to={l.to} className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-white/10 transition-colors" data-testid={`mnav-${l.label.toLowerCase()}`}>
              <l.icon className="w-5 h-5 text-neon-pink" /> {l.label}
            </Link>
          ))}
          {!user && <Link to="/login" className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-white/10">Log in</Link>}
        </div>
      )}
    </header>
  );
}
